import React from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';

const AboutMe = () => {
  

  return (
    <div id="about-me" className="min-h-screen bg-white">
      {/* Hero Section with Photo */}
      <section className="relative pt-32 pb-12 bg-gradient-to-br from-beige-50 to-rose-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            {/* Before & After Photos - Left Side */}
            <div className="flex justify-center lg:justify-end gap-4">
              {/* Left: Skin Before */}
              <div className="w-44 h-60 sm:w-56 sm:h-72 lg:w-64 lg:h-80 xl:w-72 xl:h-96 rounded-2xl overflow-hidden shadow-soft-lg relative">
                <img 
                  src="/images/Beforemystory.png" 
                  alt="What my skin used to look like" 
                  loading="lazy"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute bottom-2 left-0 right-0 text-center">
                  <span className="bg-white/80 text-charcoal-700 text-xs font-semibold px-3 py-1 rounded-full">Before</span>
                </div>
              </div>
              {/* Right: Skin Now */}
              <div className="w-44 h-60 sm:w-56 sm:h-72 lg:w-64 lg:h-80 xl:w-72 xl:h-96 rounded-2xl overflow-hidden shadow-soft-lg relative">
                <img 
                  src="/images/nowmystory.png" 
                  alt="My skin now" 
                  loading="lazy"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute bottom-2 left-0 right-0 text-center">
                  <span className="bg-white/80 text-charcoal-700 text-xs font-semibold px-3 py-1 rounded-full">Now</span>
                </div>
              </div>
            </div>

            {/* Name and Qualifications - Right Side */}
            <div className="space-y-6 text-center lg:text-left">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-playfair font-medium text-charcoal-900">
                Kate Martin
              </h1>
              <p className="text-lg sm:text-xl text-sage-600 font-medium">
                Personal Trainer • Women's Fitness Coach • Nutritionist • Wellness Advocate
              </p>
              <div className="h-1 w-24 bg-gradient-to-r from-sage-400 to-beige-400 mx-auto lg:mx-0"></div>
              <p className="text-lg text-charcoal-700 leading-relaxed">
                How pain became purpose, and struggle became strength
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* My Story */}
      <section className="pt-12 pb-4 bg-gradient-to-br from-beige-50 via-beige-100 to-rose-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-12"
          >
            <div className="text-center">
              <h2 className="text-4xl md:text-5xl font-light text-charcoal-900 mb-6">My Story</h2>
              <div className="w-24 h-1 bg-gradient-to-r from-sage-400 to-beige-400 mx-auto mb-8"></div>
            </div>

            {/* Complete Story in One Continuous Writeup */}
            <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-8 md:p-12 shadow-soft-lg border border-sage-200">
              <div className="prose prose-lg max-w-none text-charcoal-700 leading-relaxed space-y-6">
                <p className="text-xl font-medium text-charcoal-800 mb-8">
                  Hi, I'm Kate, the face and voice behind Alera.
                </p>
                
                <p>
                  From the day I was born, I've been navigating life with severe eczema, asthma, allergies, and hay fever. One doctor even claimed I was the worst eczema case in Western Australia at the time, hardly the kind of title anyone wants. My story has been anything but straightforward. I've never quite fit the "normal criteria," and because of that, I've been through almost every treatment imaginable: steroid creams, inhalers, drug trials, antibiotics, bleach baths, cold towel wraps, restrictive diets, even putting my bed linen in the freezer to kill dust mites. You name it, I've probably tried it.
                </p>

                <p>
                  And still, for years, I felt trapped. Trapped in my own skin, trapped in the endless cycle of flare-ups, trapped in a healthcare system that, in my experience, didn't have the answers I so desperately needed. I've waited months to see specialists, only to be told there was nothing more they could do except prescribe stronger steroid creams.
                </p>

                <p className="text-xl font-semibold text-sage-700 italic text-center my-8">
                  "I was told to avoid life itself—no pets, no grass, no sun, no exercise, no fun."
                </p>

                <p>
                  Living with these conditions can be isolating. The physical pain is one thing, but the mental toll; constantly worrying about how you look, if people notice, if your partner is secretly disgusted, or if the next meal will trigger another flare-up, is exhausting. I know what it's like to scratch yourself to tears, to dread the sting of a shower, and to feel too self-conscious to leave the house.
                </p>

                <div className="bg-gradient-to-r from-sage-100 to-beige-100 rounded-2xl p-8 my-8 border-l-4 border-sage-500 shadow-soft">
                  <p className="text-xl font-semibold text-sage-700 mb-4">
                    But here's the turning point.
                  </p>
                  <p>
                    After years of suppressing my symptoms, I decided to stop relying solely on prescriptions and instead focus on healing from the inside out. I've embraced natural approaches—naturopathy, herbal remedies, calming my nervous system, and making lifestyle changes that support my body rather than fight it. And while my journey is far from "finished," I finally feel like I'm moving forward.
                  </p>
                </div>

                <p>
                  I'm not a doctor, dermatologist, or health professional. What I share here is not medical advice, it's simply my story. What has worked for me may not work for everyone, but I believe that by sharing openly and honestly, I can create the kind of space I always wished existed: one where people like us don't feel so alone.
                </p>

                <p className="text-xl font-semibold text-charcoal-800 text-center my-8">
                  Alera is about hope, resilience, and finding light even when it feels like you've tried everything.
                </p>

                <div className="bg-gradient-to-r from-rose-100 to-sage-100 rounded-2xl p-8 text-center my-8 shadow-soft">
                  <p className="text-xl italic text-charcoal-800 font-medium">
                    "If you've ever felt overwhelmed, confused by conflicting information, or let down by the system that was supposed to help you, you're not alone. I'm walking this path too, and I'd love for you to join me on the journey."
                  </p>
                </div>

                <div className="text-center mt-12">
                  <p className="text-lg text-charcoal-700 mb-6">
                    Beautiful soul, if you're reading this while struggling with your own health challenges, I want you to know: you are not broken. Your body is not your enemy. And you are definitely not alone.
                  </p>
                  <p className="text-xl font-semibold text-sage-700">
                    I'm here to walk alongside you, to share what I've learned, and to remind you that healing is not just possible—it's your birthright.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Signature */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mt-4"
          >
            <p className="text-2xl text-charcoal-600 font-light mb-1 italic">
              With love,
            </p>
            
            <div className="mb-0">
              <img 
                src="/images/signature.png" 
                alt="Kate Martin signature" 
                className="w-96 h-auto mx-auto"
              />
            </div>
            
            <p className="text-lg text-charcoal-700">
              Kate Martin<br/>
              <span className="text-charcoal-500 font-medium text-sm">Founder of Alera</span>
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutMe;
